import uniqueId from 'lodash/uniqueId';
export const defaultEditorConfig = {
  clipToFrame: true,
  scrollLimit: 200,
};
export const SCALE_FACTOR = 1;

/**
 * The grey behind any personalized value in the editor, marking it as content
 * resolved at fulfillment rather than static text. Dynamic text applies it per
 * character to its tokens; vars that are personalized in their entirety apply
 * it to the whole object. Editor affordance only, never persisted.
 */
export const PERSONALIZED_VALUE_BACKGROUND = '#d4d1cb';
export const FONT_SCALE_FACTOR = 1; //0.675;
export const defaultGridSize = 15;
export const bufferModes = ['fit', 'fill'];
export const bleed = 0;
export const PROPERTIES_TO_INCLUDE = [
  'metadata',
  'keys',
  'subtype',
  'name',
  'text',
  'id',
  'originalText',
  'keyValues',
];

export const zoomLevels = [3, 2, 1.25, 1, 0.75, 0.5, 0.25, 0.1];

export const SCROLL_SPEED = 30;
export const FRAME_MARGIN = 200;
export const DEFAULT_HEIGHT = 960;
export const DEFAULT_WIDTH = 480;
export const FRAMES_MARGIN = 80;

export const initialSizeFormat = {
  id: 0,
  formatHeight: '8.0',
  formatWidth: '4.0',
  formatUnit: 'in',
  label: '4.0x8.0 in',
  isPortrait: true,
  pixelHeight: 2400,
  pixelWidth: 1200,
};

export const defaultGrid = {
  size: 15,
  enabled: false,
  color: '#e2e2e2',
  name: 'bgGrid',
};

export const elementBoxStyle = {
  borderColor: '#7f66f1',
  borderScaleFactor: 2.75,
  stroke: '0.1875rem',
  cornerColor: '#4e50d6',
  cornerStyle: 'circle',
  transparentCorners: false,
};

export const fabricObjects = {
  STATIC_TEXT: 'txt:static',
  DYNAMIC_TEXT: 'txt_template:dynamic',
  DYNAMIC_TEXT_OLD: 'txt:dynamic',
  DYNAMIC_IMAGE: 'img:dynamic',
  DYNAMIC_QR: 'qrc:dynamic',
  DYNAMIC_BARCODE: 'barcode:dynamic',
  STATIC_IMAGE: 'img:static',
  STATIC_QR: 'qrc:static',
  STATIC_BARCODE: 'barcode:static',
  SHAPE: 'shape:static',
  CONDITIONAL_TEXT: 'cond_txt_template:dynamic',
  CONDITIONAL_IMAGE: 'cond_img:dynamic',
  CONDITIONAL_QR: 'cond_qrc:dynamic',
  SUPPLIED_VALUE: 'supplied_value:static',
};

export const objectTypes = {
  FRAME: 'Frame',
  BACKGROUND_IMAGE: 'BackgroundImage',
  DYNAMIC_TEXT: 'DynamicText',
  DYNAMIC_IMAGE: 'DynamicImage',
  DYNAMIC_QR: 'DynamicImage',
  DYNAMIC_BARCODE: 'DynamicImage',
  STATIC_IMAGE: 'StaticImage',
  STATIC_QR: 'DynamicImage',
  STATIC_BARCODE: 'DynamicImage',
  CONDITIONAL_TEXT: 'ConditionalText',
  CONDITIONAL_IMAGE: 'DynamicImage',
  CONDITIONAL_QR: 'DynamicImage',
  SUPPLIED_VALUE: 'SuppliedValue',
};

export const canvasEvents = {
  ADDED: 'added',
  MOUSE_DOWN: 'mouse:down',
  SELECTION_CREATED: 'selection:created',
};

export const ZOOM_MODE = {
  FIT: 'fit',
  FILL: 'fill',
};

export const contextMenuType = {
  DEFAULT: 'Default',
  TEXT: 'text',
  IMAGE: 'image',
};

export const defaultFrameOptions = {
  id: uniqueId(objectTypes.FRAME),
  name: 'frame',
  width: 600,
  height: 400,
  fill: '#ffffff',
  backgroundColor: '#ffffff',
};

export const exclusiveTextProperties = [
  'textAlign',
  'fontFamily',
  'fontSize',
  'fontWeight',
  'charspacing',
  'lineheight',
];

const getCopyStyleVector = () => {
  const copyStyleVector = `
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path fillRule="evenodd" clipRule="evenodd" d="M16 3.5H5a.5.5 0 0 0-.5.5v1.5A.5.5 0 0 0 5 6h11a.5.5 0 0 0 .5-.5V4a.5.5 0 0 0-.5-.5zM5 2a2 2 0 0 0-2 2v1.5a2 2 0 0 0 2 2h11a2 2 0 0 0 2-2v-.25h.5a.75.75 0 0 1 .75.75v2.5a.75.75 0 0 1-.75.75h-5.75a2.25 2.25 0 0 0-2.25 2.25v1.563A2 2 0 0 0 9 15v5a2 2 0 0 0 2 2h.5a2 2 0 0 0 2-2v-5a2 2 0 0 0-1.5-1.937V11.5a.75.75 0 0 1 .75-.75h5.75a2.25 2.25 0 0 0 2.25-2.25V6a2.25 2.25 0 0 0-2.25-2.25h-.515A2 2 0 0 0 16 2H5zm7 13a.5.5 0 0 0-.5-.5H11a.5.5 0 0 0-.5.5v5a.5.5 0 0 0 .5.5h.5a.5.5 0 0 0 .5-.5v-5z" fill="currentColor"></path></svg>
	`;
  return `data:image/svg+xml;base64,${window.btoa(copyStyleVector)}`;
};

const getOpenHandStyleVector = () => {
  const openHandStyleVector = `
  <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="24px" height="24px"><g id="surface38654920"><path style="fillRule:nonzero;fill:rgb(100%,100%,100%);fill-opacity:1;stroke-width:10;stroke-linecap:butt;stroke-linejoin:round;stroke:rgb(0%,0%,0%);stroke-opacity:1;stroke-miterlimit:10;" d="M 169.50046 56.499539 C 175.355928 56.499539 180.097896 61.241508 180.097896 67.096975 C 180.097896 72.952443 180.097896 100.847505 180.097896 117.511589 C 180.097896 134.194872 180.097896 157.827922 174.242429 174.45361 C 164.508914 202.118293 127.763457 220.970978 91.017999 206.572288 C 80.132589 202.310276 62.067033 191.885624 53.370224 168.694134 C 44.558225 145.157075 36.130192 119.04745 33.8456 110.984183 C 31.561008 102.920917 32.021766 96.201528 38.990732 92.822635 C 44.270252 90.269267 51.507993 95.049633 54.253344 100.041178 C 55.750807 102.76733 64.178841 118.298718 69.592748 128.28181 C 70.802238 130.528005 74.200329 129.664084 74.219527 127.110716 C 74.29632 102.920917 74.353915 62.930954 74.373113 42.120047 C 74.373113 36.398968 79.019091 31.772188 84.759369 31.772188 C 90.46125 31.772188 95.107227 36.398968 95.126426 42.120047 C 95.18402 61.606275 95.29921 95.702373 95.337606 105.954241 C 95.337606 107.912462 96.911863 109.467521 98.870085 109.467521 C 100.828307 109.467521 102.402564 107.874066 102.402564 105.935042 C 102.402564 103.97682 102.402564 37.627656 102.402564 31.772188 C 102.402564 25.935919 107.144532 21.193951 113 21.193951 C 118.855467 21.193951 123.597436 25.935919 123.597436 31.772188 C 123.597436 37.627656 123.597436 103.996019 123.597436 105.935042 C 123.597436 107.893264 125.171693 109.467521 127.129914 109.467521 C 129.068938 109.467521 130.662393 107.893264 130.662393 105.935042 C 130.662393 103.996019 130.662393 48.225092 130.662393 42.369624 C 130.662393 36.514157 135.404362 31.772188 141.240631 31.772188 C 147.096098 31.772188 151.838067 36.514157 151.838067 42.369624 C 151.838067 48.225092 151.838067 114.727843 151.838067 116.62847 C 151.838067 118.548295 153.431522 120.103354 155.351347 120.122552 C 157.271173 120.122552 158.903024 118.567493 158.903024 116.62847 C 158.903024 114.689446 158.903024 72.952443 158.903024 67.096975 C 158.903024 61.241508 163.644993 56.499539 169.50046 56.499539 Z M 169.50046 56.499539 " transform="matrix(0.203469,0,0,0.203469,1.008,1.008)"/><path style=" stroke:none;fillRule:nonzero;fill:rgb(100%,100%,100%);fill-opacity:1;" d="M 35.496094 12.503906 C 36.6875 12.503906 37.652344 13.46875 37.652344 14.660156 C 37.652344 15.851562 37.652344 21.527344 37.652344 24.917969 C 37.652344 28.3125 37.652344 33.121094 36.460938 36.503906 C 34.480469 42.132812 27.003906 45.96875 19.527344 43.039062 C 17.3125 42.171875 13.636719 40.050781 11.867188 35.332031 C 10.074219 30.542969 8.359375 25.230469 7.894531 23.589844 C 7.429688 21.949219 7.523438 20.582031 8.941406 19.894531 C 10.015625 19.375 11.488281 20.347656 12.046875 21.363281 C 12.351562 21.917969 14.066406 25.078125 15.167969 27.109375 C 15.414062 27.566406 16.105469 27.390625 16.109375 26.871094 C 16.125 21.949219 16.136719 13.8125 16.140625 9.578125 C 16.140625 8.414062 17.085938 7.472656 18.253906 7.472656 C 19.414062 7.472656 20.359375 8.414062 20.363281 9.578125 C 20.375 13.542969 20.398438 20.480469 20.40625 22.566406 C 20.40625 22.964844 20.726562 23.28125 21.125 23.28125 C 21.523438 23.28125 21.84375 22.957031 21.84375 22.5625 C 21.84375 22.164062 21.84375 8.664062 21.84375 7.472656 C 21.84375 6.285156 22.808594 5.320312 24 5.320312 C 25.191406 5.320312 26.15625 6.285156 26.15625 7.472656 C 26.15625 8.664062 26.15625 22.167969 26.15625 22.5625 C 26.15625 22.960938 26.476562 23.28125 26.875 23.28125 C 27.269531 23.28125 27.59375 22.960938 27.59375 22.5625 C 27.59375 22.167969 27.59375 10.820312 27.59375 9.628906 C 27.59375 8.4375 28.558594 7.472656 29.746094 7.472656 C 30.9375 7.472656 31.902344 8.4375 31.902344 9.628906 C 31.902344 10.820312 31.902344 24.351562 31.902344 24.738281 C 31.902344 25.128906 32.226562 25.445312 32.617188 25.449219 C 33.007812 25.449219 33.339844 25.132812 33.339844 24.738281 C 33.339844 24.34375 33.339844 15.851562 33.339844 14.660156 C 33.339844 13.46875 34.304688 12.503906 35.496094 12.503906 Z M 35.496094 12.503906 "/></g></svg>
  `;
  return `data:image/svg+xml;base64,${window.btoa(openHandStyleVector)}`;
};

const getRockHandVector = () => {
  const rockHandVector = `
  <svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" viewBox="0 0 48 48" width="24px" height="24px"><g id="surface38948706"><path style="fillRule:nonzero;fill:rgb(100%,100%,100%);fill-opacity:1;stroke-width:10;stroke-linecap:butt;stroke-linejoin:round;stroke:rgb(0%,0%,0%);stroke-opacity:1;stroke-miterlimit:10;" d="M 180.097896 89.309355 C 180.097896 105.973439 180.097896 129.625687 174.223231 146.270573 C 164.528112 173.916059 127.763457 192.768744 91.037198 178.370053 C 80.190184 174.127239 62.297412 163.779381 53.562206 140.895062 C 53.504612 140.799071 50.816856 134.156475 49.261798 130.451212 C 43.214348 115.937332 47.495558 99.119662 59.782441 89.270958 L 67.096975 83.434689 L 67.096975 116.532478 C 67.096975 118.471502 68.69043 120.064957 70.629454 120.064957 C 72.568477 120.064957 74.161933 118.471502 74.161933 116.532478 L 74.161933 62.470196 C 74.161933 53.389422 82.705155 49.472978 90.038888 49.472978 C 93.398583 49.472978 95.337606 52.256725 95.337606 56.249962 L 95.337606 77.752006 C 95.337606 79.69103 96.931061 81.246088 98.870085 81.246088 C 100.809109 81.246088 102.402564 79.671831 102.402564 77.713609 L 102.402564 52.967061 C 102.402564 47.111593 107.144532 42.369624 113 42.369624 C 118.855467 42.369624 123.597436 47.111593 123.597436 52.967061 L 123.597436 77.713609 C 123.597436 79.69103 125.190891 81.246088 127.129914 81.246088 C 129.068938 81.246088 130.662393 79.69103 130.662393 77.713609 L 130.662393 56.249962 C 130.662393 52.256725 132.601417 49.472978 135.961111 49.472978 C 143.294844 49.472978 151.838067 53.389422 151.838067 62.470196 L 151.838067 84.893756 C 151.838067 86.794383 153.431522 88.349442 155.332149 88.387839 C 157.290371 88.387839 158.903024 86.83278 158.903024 84.893756 L 158.903024 70.379877 C 158.903024 66.38664 160.842048 63.602893 164.201742 63.602893 C 171.861845 63.602893 180.097896 74.85307 180.097896 89.309355 Z M 180.097896 89.309355 " transform="matrix(0.203469,0,0,0.203469,1.008,1.008)"/><path style=" stroke:none;fillRule:nonzero;fill:rgb(100%,100%,100%);fill-opacity:1;" d="M 37.652344 19.179688 C 37.652344 22.570312 37.652344 27.382812 36.457031 30.769531 C 34.484375 36.394531 27.003906 40.230469 19.53125 37.300781 C 17.324219 36.4375 13.683594 34.332031 11.90625 29.675781 C 11.894531 29.65625 11.347656 28.304688 11.03125 27.550781 C 9.800781 24.597656 10.671875 21.175781 13.171875 19.171875 L 14.660156 17.984375 L 14.660156 24.71875 C 14.660156 25.113281 14.984375 25.4375 15.378906 25.4375 C 15.773438 25.4375 16.097656 25.113281 16.097656 24.71875 L 16.097656 13.71875 C 16.097656 11.871094 17.835938 11.074219 19.328125 11.074219 C 20.011719 11.074219 20.40625 11.640625 20.40625 12.453125 L 20.40625 16.828125 C 20.40625 17.222656 20.730469 17.539062 21.125 17.539062 C 21.519531 17.539062 21.84375 17.21875 21.84375 16.820312 L 21.84375 11.785156 C 21.84375 10.59375 22.808594 9.628906 24 9.628906 C 25.191406 9.628906 26.15625 10.59375 26.15625 11.785156 L 26.15625 16.820312 C 26.15625 17.222656 26.480469 17.539062 26.875 17.539062 C 27.269531 17.539062 27.59375 17.222656 27.59375 16.820312 L 27.59375 12.453125 C 27.59375 11.640625 27.988281 11.074219 28.671875 11.074219 C 30.164062 11.074219 31.902344 11.871094 31.902344 13.71875 L 31.902344 18.28125 C 31.902344 18.667969 32.226562 18.984375 32.613281 18.992188 C 33.011719 18.992188 33.339844 18.675781 33.339844 18.28125 L 33.339844 15.328125 C 33.339844 14.515625 33.734375 13.949219 34.417969 13.949219 C 35.976562 13.949219 37.652344 16.238281 37.652344 19.179688 Z M 37.652344 19.179688 "/></g></svg>
  `;

  return `data:image/svg+xml;base64,${window.btoa(rockHandVector)}`;
};

export const getCopyStyleCursor = () => {
  return `url(${getCopyStyleVector()}), crosshair`;
};

export const getPanningStyleCursor = () =>
  `url(${getOpenHandStyleVector()}), crosshair`;

export const getRockHandStyleCursor = () => {
  return `url(${getRockHandVector()}), crosshair`;
};

export const copyStyleProps = {
  StaticText: [
    'fill',
    'opacity',
    'stroke',
    'strokeWidth',
    'textAlign',
    'fontWeight',
    'fontFamily',
    'fontSize',
    'underline',
    'shadow',
    'angle',
    'width',
    'height',
    'charSpacing',
    'lineHeight',
  ],
  DynamicText: [
    'fill',
    'opacity',
    'stroke',
    'strokeWidth',
    'textAlign',
    'fontWeight',
    'fontFamily',
    'fontSize',
    'underline',
    'shadow',
    'angle',
    'width',
    'height',
    'charSpacing',
    'lineHeight',
  ],
  StaticImage: [
    'opacity',
    'stroke',
    'strokeWidth',
    'shadow',
    'angle',
    'width',
    'height',
  ],
  DynamicImage: [
    'opacity',
    'stroke',
    'strokeWidth',
    'shadow',
    'angle',
    'width',
    'height',
  ],
  StaticPath: [
    'fill',
    'opacity',
    'stroke',
    'strokeWidth',
    'shadow',
    'angle',
    'width',
    'height',
  ],
  ConditionalText: [
    'fill',
    'opacity',
    'stroke',
    'strokeWidth',
    'textAlign',
    'fontWeight',
    'fontFamily',
    'fontSize',
    'underline',
    'shadow',
    'angle',
    'width',
    'height',
    'charSpacing',
    'lineHeight',
  ],
  SuppliedValue: [
    'fill',
    'opacity',
    'stroke',
    'strokeWidth',
    'textAlign',
    'fontWeight',
    'fontFamily',
    'fontSize',
    'underline',
    'shadow',
    'angle',
    'width',
    'height',
    'charSpacing',
    'lineHeight',
  ],
  // ConditionalImage: [
  //   'opacity',
  //   'stroke',
  //   'strokeWidth',
  //   'shadow',
  //   'angle',
  //   'width',
  //   'height',
  // ],
};

export const colorPickerList = {
  recommend: [
    '#000',
    '#FFF',
    '#FF5757',
    '#CB6CE6',
    '#FF66C4',
    '#5271FF',
    '#38B6FF',
    '#5CE1E6',
    '#7ED957',
    '#84763D',
    '#C1FF72',
    '#FFDE59',
    '#FFBD59',
    '#FF914D',
  ],
  solid: [
    'Black #000000',
    'Dark Gray #545454',
    'Gray #737373',
    'Gray #a6a6a6',
    'Light Gray #d9d9d9',
    'White #ffffff',
    'Bright red #ff3131',
    'Coral red #ff5757',
    'Pink #ff66c4',
    'Magenta #cb6ce6',
    'Purple #8c52ff',
    'Violet #5e17eb',
    'Dark Turquoise #0097b2',
    'Aqua blue #0cc0df',
    'Turquoise blue #5ce1e6',
    'Light blue #38b6ff',
    'Royal blue #5271ff',
    'Cobalt blue #004aad',
    'Green #00bf63',
    'Grass green #7ed957',
    'Lime #c1ff72',
    'Yellow #ffde59',
    'Peach #ffbd59',
    'Orange #ff914d',
  ],
  gradient: [
    'Linear gradient 90°: #000000, #737373',
    'Linear gradient 90°: #000000, #737373',
    'Linear gradient 90°: #000000, #c89116',
    'Linear gradient 90°: #000000, #c89116',
    'Linear gradient 90°: #000000, #3533cd',
    'Linear gradient 90°: #000000, #3533cd',
    'Linear gradient 90°: #a6a6a6, #ffffff',
    'Linear gradient 90°: #a6a6a6, #ffffff',
    'Linear gradient 90°: #fff7ad, #ffa9f9',
    'Linear gradient 90°: #fff7ad, #ffa9f9',
    'Linear gradient 90°: #cdffd8, #94b9ff',
    'Linear gradient 90°: #cdffd8, #94b9ff',
    'Linear gradient 90°: #ff3131, #ff914d',
    'Linear gradient 90°: #ff3131, #ff914d',
    'Linear gradient 90°: #ff5757, #8c52ff',
    'Linear gradient 90°: #ff5757, #8c52ff',
    'Linear gradient 90°: #5170ff, #ff66c4',
    'Linear gradient 90°: #5170ff, #ff66c4',
    'Linear gradient 90°: #004aad, #cb6ce6',
    'Linear gradient 90°: #004aad, #cb6ce6',
    'Linear gradient 90°: #8c52ff, #5ce1e6',
    'Linear gradient 90°: #8c52ff, #5ce1e6',
    'Linear gradient 90°: #5de0e6, #004aad',
    'Linear gradient 90°: #5de0e6, #004aad',
    'Linear gradient 90°: #8c52ff, #00bf63',
    'Linear gradient 90°: #8c52ff, #00bf63',
    'Linear gradient 90°: #0097b2, #7ed957',
    'Linear gradient 90°: #0097b2, #7ed957',
    'Linear gradient 90°: #0cc0df, #ffde59',
    'Linear gradient 90°: #0cc0df, #ffde59',
    'Linear gradient 90°: #ffde59, #ff914d',
    'Linear gradient 90°: #ffde59, #ff914d',
    'Linear gradient 90°: #ff66c4, #ffde59',
    'Linear gradient 90°: #ff66c4, #ffde59',
    'Linear gradient 90°: #8c52ff, #ff914d',
    'Linear gradient 90°: #8c52ff, #ff914d',
  ],
};
